import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import MapView from "../map-view/index";
import { ImageModal } from "../images/image-modal";
import {
  MapPin,
  Home,
  Maximize2,
  DollarSign,
  Bed,
  Bath,
  Eye,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { getBuildingSquareFeet } from "../../utils/property-calculations";

export const PropertyCard = ({
  additionalDetailsRenderer,
  backgroundColor = "transparent",
  imageHeight = 180,
  property,
  redirectOnClick = true,
  showImageModalOnClick = false,
  showPrices = false,
  target,
  width,
}) => {
  const [open, setOpen] = useState(false);
  const {
    city,
    estimatedValue = 0,
    latitude,
    longitude,
    photoUrl,
    photoUrls = [],
    photosAvailable = false,
    state,
    streetAddress,
    zip,
    bedrooms,
    bathrooms,
  } = property;
  const squareFeet = getBuildingSquareFeet(property) || 0;

  const Wrapper = redirectOnClick ? Link : "div";

  const handleClose = () => {
    setOpen(false);
  };

  const formatPrice = (price) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <>
      <Card
        className="group bg-white hover:shadow-2xl shadow-lg transition-all duration-300 overflow-hidden border-2 border-gray-200/60 hover:border-primary/60 w-full hover:-translate-y-1"
        style={{ backgroundColor, ...(width && { width }) }}
      >
        {/* Image Section */}
        <div
          className="relative overflow-hidden"
          style={{ height: imageHeight }}
        >
          {photosAvailable ? (
            <>
              <img
                onClick={(e) => {
                  if (showImageModalOnClick) {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(true);
                  }
                }}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                style={{
                  cursor: showImageModalOnClick ? "pointer" : "default",
                }}
                src={photoUrl || photoUrls[0]}
                alt={streetAddress}
              />
              {showImageModalOnClick && (
                <div
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(true);
                  }}
                >
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Maximize2 className="text-white h-6 w-6" />
                    <span className="text-white text-sm font-semibold">
                      View Photos
                    </span>
                  </div>
                </div>
              )}
              {photoUrls.length > 1 && (
                <Badge
                  variant="secondary"
                  className="absolute top-3 right-3 bg-black/70 text-white border-0 text-xs font-semibold shadow-lg"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  {photoUrls.length} photos
                </Badge>
              )}
            </>
          ) : (
            <div className="bg-muted h-full relative">
              <MapView
                latitude={latitude}
                longitude={longitude}
                width={width || 340}
                height={imageHeight}
              />
              <Badge
                variant="secondary"
                className="absolute top-3 right-3 bg-black/70 text-white border-0 text-xs font-semibold shadow-lg"
              >
                <MapPin className="h-3 w-3 mr-1" />
                Map View
              </Badge>
            </div>
          )}

          {/* Quick View Overlay on Hover */}
          {estimatedValue > 0 && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <p className="text-white text-lg font-bold">
                {formatPrice(estimatedValue)}
              </p>
            </div>
          )}
        </div>

        {/* Content Section */}
        <CardContent className="p-3">
          <Wrapper
            className="no-underline block"
            target={target}
            to={`/property/${property?.propertyId || property?.id}`}
          >
            {/* Location */}
            <div className="flex items-start gap-1.5 mb-1.5">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">
                  {city}, {state} {zip}
                </p>
              </div>
            </div>

            {/* Street Address */}
            <div className="flex items-start gap-1.5 mb-2">
              <Home className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-foreground line-clamp-2 flex-1">
                {streetAddress || "Address not available"}
              </h3>
            </div>

            {/* Property Details */}
            {(bedrooms || bathrooms || squareFeet) && (
              <div className="flex gap-3 mb-2 text-xs">
                {bedrooms && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                          <Bed className="h-3.5 w-3.5" />
                          <span className="font-semibold text-foreground">
                            {bedrooms}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{bedrooms} Bedrooms</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {bathrooms && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                          <Bath className="h-3.5 w-3.5" />
                          <span className="font-semibold text-foreground">
                            {bathrooms}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{bathrooms} Bathrooms</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {squareFeet > 0 && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {squareFeet.toLocaleString()}
                    </span>
                    <span>sq ft</span>
                  </div>
                )}
              </div>
            )}

            {/* Price Information */}
            {showPrices && (
              <div className="border-t pt-2 mt-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Total Cost
                  </span>
                  <span className="text-sm font-bold text-primary">
                    {formatPrice(estimatedValue)}
                  </span>
                </div>
                {squareFeet > 0 && estimatedValue > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Price per sq ft
                    </span>
                    <span className="text-xs font-semibold text-foreground">
                      {formatPrice(Math.ceil(estimatedValue / squareFeet))}
                    </span>
                  </div>
                )}
                {additionalDetailsRenderer &&
                  additionalDetailsRenderer(property)}
              </div>
            )}

            {/* View Details Button */}
            {!showPrices && (
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-1.5 text-xs h-8 group-hover:bg-primary group-hover:text-primary-foreground transition-all group-hover:shadow-md"
              >
                <Eye className="h-3 w-3 mr-1" />
                View Details
              </Button>
            )}
          </Wrapper>
        </CardContent>
      </Card>

      {showImageModalOnClick && (
        <ImageModal
          open={open}
          onClose={handleClose}
          url={property?.photoUrl}
        />
      )}
    </>
  );
};

export default PropertyCard;
